import React, { useState, forwardRef, useImperativeHandle } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';

const OwnerInformation = forwardRef(({ owners, onChange }, ref) => {
  const [localOwners, setLocalOwners] = useState(owners || [{ name: '', email: '' }]);
  const [errors, setErrors] = useState({});

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex?.test(email);
  };

  const handleOwnerChange = (index, field, value) => {
    const updatedOwners = [...localOwners];
    updatedOwners[index][field] = value;
    setLocalOwners(updatedOwners);
    onChange(updatedOwners);

    const errorKey = `${index}-${field}`;
    if (errors?.[errorKey]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors?.[errorKey];
        return newErrors;
      });
    }
  };

  const addOwner = () => {
    const newOwners = [...localOwners, { name: '', email: '' }];
    setLocalOwners(newOwners);
    onChange(newOwners);
  };

  const removeOwner = (index) => {
    if (localOwners?.length > 1) {
      const updatedOwners = localOwners?.filter((_, i) => i !== index);
      setLocalOwners(updatedOwners);
      onChange(updatedOwners);

      const newErrors = { ...errors };
      Object.keys(newErrors)?.forEach(key => {
        if (key?.startsWith(`${index}-`)) {
          delete newErrors?.[key];
        }
      });
      setErrors(newErrors);
    }
  };

  const validateOwner = (index) => {
    const owner = localOwners?.[index];
    const newErrors = {};

    if (!owner?.name?.trim()) {
      newErrors[`${index}-name`] = 'Owner name is required';
    }

    if (!owner?.email?.trim()) {
      newErrors[`${index}-email`] = 'Owner email is required';
    } else if (!validateEmail(owner?.email)) {
      newErrors[`${index}-email`] = 'Invalid email format';
    }

    setErrors(prev => ({ ...prev, ...newErrors }));
    return Object.keys(newErrors)?.length === 0;
  };

  const validateAllOwners = () => {
    const newErrors = {};
    let isValid = true;

    localOwners?.forEach((owner, index) => {
      // Only validate if owner has any data entered
      const hasData = owner?.name?.trim() || owner?.email?.trim();
      
      if (hasData) {
        if (!owner?.name?.trim()) {
          newErrors[`${index}-name`] = 'Owner name is required';
          isValid = false;
        }

        if (!owner?.email?.trim()) {
          newErrors[`${index}-email`] = 'Owner email is required';
          isValid = false;
        } else if (!validateEmail(owner?.email)) {
          newErrors[`${index}-email`] = 'Invalid email format';
          isValid = false;
        }
      }
    });

    setErrors(newErrors);
    return isValid;
  };

  // Expose validation method to parent via ref
  useImperativeHandle(ref, () => ({
    validate: validateAllOwners,
    getData: () => localOwners?.filter(owner => owner?.name?.trim() && owner?.email?.trim())
  }));

  return (
    <div className="bg-card rounded-lg p-4 md:p-6 lg:p-8 shadow-elevation-md">
      <div className="flex items-center justify-between mb-4 md:mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg bg-secondary/10 flex items-center justify-center">
            <Icon name="Users" size={20} className="text-secondary" />
          </div>
          <div>
            <h3 className="text-lg md:text-xl font-semibold text-foreground">Owner Information</h3>
            <p className="text-sm text-muted-foreground">Add property owner details</p>
          </div>
        </div>
        
        {localOwners?.length < 4 && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={addOwner}
            iconName="Plus"
            iconPosition="left"
            className="hidden sm:flex"
          >
            Add Owner
          </Button>
        )}
      </div>
      <div className="space-y-4 md:space-y-6">
        {localOwners?.map((owner, index) => (
          <div key={index} className="relative">
            <div className="bg-muted/30 rounded-lg p-4 md:p-5 space-y-4">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-foreground">
                  Owner {index + 1}
                </span>
                {localOwners?.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeOwner(index)}
                    className="p-1.5 rounded-lg hover:bg-destructive/10 text-destructive transition-colors"
                    aria-label={`Remove owner ${index + 1}`}
                  >
                    <Icon name="Trash2" size={16} />
                  </button>
                )}
              </div>

              <Input
                label="Owner Name"
                type="text"
                placeholder="John Smith"
                value={owner?.name}
                onChange={(e) => handleOwnerChange(index, 'name', e?.target?.value)}
                onBlur={() => validateOwner(index)}
                error={errors?.[`${index}-name`]}
                required
              />

              <Input
                label="Owner Email"
                type="email"
                placeholder="john.smith@example.com"
                value={owner?.email}
                onChange={(e) => handleOwnerChange(index, 'email', e?.target?.value)}
                onBlur={() => validateOwner(index)}
                error={errors?.[`${index}-email`]}
                description="Reports will be sent to this email address"
                required
              />
            </div>
          </div>
        ))}

        {localOwners?.length < 4 && (
          <Button
            type="button"
            variant="outline"
            onClick={addOwner}
            iconName="Plus"
            iconPosition="left"
            fullWidth
            className="sm:hidden"
          >
            Add Another Owner
          </Button>
        )}

        {localOwners?.length >= 4 && (
          <div className="flex items-center gap-2 p-3 bg-warning/10 rounded-lg">
            <Icon name="Info" size={16} className="text-warning flex-shrink-0" />
            <p className="text-sm text-warning">Maximum of 4 owners allowed per property</p>
          </div>
        )}
      </div>
    </div>
  );
});

export default OwnerInformation;